import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TypingParticipant } from "@playbookai/player-ai-client";
import { firstName } from "./rail-message-context.js";

/** How long a name stays without a fresh signal from that person. */
export const TYPING_DISPLAY_TTL_MS = 5_000;
/** At most one "typing" signal per interval while the draft keeps changing. */
export const TYPING_SIGNAL_INTERVAL_MS = 2_500;

interface SeenTypist {
  readonly name: string;
  readonly expiresAt: string;
  readonly seenAt: number;
}

/**
 * Task 15 — who ELSE is typing in a group conversation, and this tab's own
 * signal. Individual conversations get neither.
 *
 * Names expire HERE as well as on the server: a person stays listed until
 * `TYPING_DISPLAY_TTL_MS` after the frame that last refreshed them, measured on
 * this clock when it arrived. The server's `expires_at` only says whether a
 * frame refreshed someone; it is never compared with this clock, so a skewed
 * clock cannot hide or pin a name. A tab that closed or lost its connection
 * sends no more refreshes, so its name leaves on its own.
 */
export function useTypingIndicator({
  enabled,
  participants,
  currentUserId,
  conversationId,
  signalTyping,
}: {
  readonly enabled: boolean;
  readonly participants: readonly TypingParticipant[];
  readonly currentUserId: string | null;
  readonly conversationId: string | null;
  readonly signalTyping: (state: "typing" | "idle") => void;
}): {
  readonly typingNames: readonly string[] | undefined;
  readonly onComposerActivity:
    | ((activity: "typing" | "idle") => void)
    | undefined;
} {
  const [seen, setSeen] = useState<ReadonlyMap<string, SeenTypist>>(
    () => new Map(),
  );
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const now = Date.now();
    setSeen((previous) => {
      const next = new Map<string, SeenTypist>();
      for (const participant of participants) {
        // The viewer's own other tabs and devices are never listed.
        if (participant.user_id === currentUserId) continue;
        const before = previous.get(participant.user_id);
        next.set(participant.user_id, {
          name: firstName(participant.display_name) ?? "Someone",
          expiresAt: participant.expires_at,
          seenAt:
            before?.expiresAt === participant.expires_at ? before.seenAt : now,
        });
      }
      return next;
    });
    setClock(now);
  }, [currentUserId, participants]);
  useEffect(() => {
    if (!enabled || seen.size === 0) return;
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [enabled, seen]);
  const typingNames = useMemo(
    () =>
      enabled
        ? [...seen.values()]
            .filter((typist) => typist.seenAt + TYPING_DISPLAY_TTL_MS > clock)
            .map((typist) => typist.name)
        : undefined,
    [clock, enabled, seen],
  );

  const signalRef = useRef(signalTyping);
  signalRef.current = signalTyping;
  /** When this tab last said "typing"; 0 while it is not typing. */
  const lastSignalRef = useRef(0);
  const onComposerActivity = useCallback(
    (activity: "typing" | "idle"): void => {
      if (activity === "typing") {
        const now = Date.now();
        if (now - lastSignalRef.current < TYPING_SIGNAL_INTERVAL_MS) return;
        lastSignalRef.current = now;
        signalRef.current("typing");
        return;
      }
      // Sent, or the draft was cleared: say so at once rather than expiring.
      if (lastSignalRef.current === 0) return;
      lastSignalRef.current = 0;
      signalRef.current("idle");
    },
    [],
  );
  // Leaving a conversation mid-draft stops this tab's signal; the name it
  // left behind elsewhere expires on its own.
  useEffect(
    () => () => {
      lastSignalRef.current = 0;
    },
    [conversationId],
  );
  return {
    typingNames,
    onComposerActivity: enabled ? onComposerActivity : undefined,
  };
}
